import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import PageHeader from 'components/project/page_header';
import { Button } from 'components/ui/button';
import { put } from 'helpers/api';
import { fetchShareableTokens } from 'components/project/api';
import { useBuildoutOsDisplay } from 'components/buildout_os_display';

import { faPaperPlane, faXmark, faChevronDown, faChevronRight, faEye, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faCalendar, faEye as faEyeOutline } from '@fortawesome/pro-regular-svg-icons';
import { Col, Row } from 'react-bootstrap-5';
import { Calendar, type DateRange } from '@buildoutinc/blueprint-react/ui/Calendar';
import { Popover } from '@buildoutinc/blueprint-react/ui/Popover';

import { type Props as CompanyCardProps } from './company_card';
import { type Props as SyndicationProps } from './syndication_card';
import { type Props as LeadsProps } from './leads_card';
import { type Props as WebsiteTrafficProps } from './website_traffic';
import { type Props as ReferralProps } from './referral_source_card';
import { type EmailAnalyticsProps } from './email_analytics_card';
import { type ActivitySummaryProps } from './activity_summary_card';
import type { CustomCards, CustomCardProps } from './custom_card';
import {
  ActivityFunnelWithPreview,
  ActivitySummaryCardWithPreview,
  BasicCardWithPreview,
  CompanyCardWithPreview,
  CustomCardWithPreview,
  DocumentStatsCardWithPreview,
  EmailAnalyticsWithPreview,
  EmailDetailsWithPreview,
  LeadsCardWithPreview,
  ReferralSourceCardWithPreview,
  SyndicationCardWithPreview,
  WebsiteTrafficWithPreview,
  type Card as CardProps,
  initialDefaultCards,
  type ShareableToken,
  type Collaborator,
  useWindowSize,
  cardMap,
  leadsTableColumnsMap
} from './util';
import ShareClientReportModal from './share_client_report_modal';
import LoadingCard from './loading_card';
import CustomCardModal from './custom_card_modal';
import type { Project } from 'bundles/client_reports/client_report_types';
import DatePicker from 'react-datepicker';
import Dropdown from 'components/dropdown';
import { DropdownItem as MenuItem } from 'react-bootstrap-5';

import './styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from 'components/ui/alert';
import { Checkbox } from 'components/ui/checkbox';
import Toggle from 'components/toggle';

type Metrics = CompanyCardProps &
  CustomCards &
  SyndicationProps &
  LeadsProps &
  ReferralProps &
  EmailAnalyticsProps &
  WebsiteTrafficProps &
  ActivitySummaryProps & {
    casSigned: number;
    casViewed: number;
    daysOnMarket: number;
    totalLeads: number;
    documentDownloads: number;
    documentViews: number;
    totalPublicDocViews: number;
    uniqueLockedViews: number;
    documentDownloadsByMonth: Record<string, number>;
    documentViewsByMonth: Record<string, number>;
  };

type Props = {
  canManageLeads?: boolean;
  collaborators?: Collaborator[];
  defaultCards: CardProps[];
  initialEndDate?: string;
  fetchFastMetrics: (startDate: string, endDate: string) => Promise<Metrics>;
  fetchAnalyticsMetrics: (startDate: string, endDate: string) => Promise<Metrics>;
  preview: boolean;
  project: Project,
  projectId: number;
  initialStartDate?: string;
}

export const GRAPH_HEIGHT = 250;
const MAX_NUM_COLUMNS = 12;

function setupCardsData(defaultCards?: CardProps[]): CardProps[] {
  return defaultCards?.length ? defaultCards : initialDefaultCards;
}

function normalizeDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;

  // Match plain date YYYY-MM-DD (no time component)
  const plainDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (plainDateMatch) {
    const [, yStr, mStr, dStr] = plainDateMatch;
    const y = Number(yStr);
    const m = Number(mStr); // 1–12
    const d = Number(dStr);

    // Create in local timezone at noon (reduces DST boundary surprises)
    const localDate = new Date(y, m - 1, d, 12, 0, 0, 0);

    // Validate it “round-trips” (invalid dates like 2025-02-30 will roll over)
    if (
      localDate.getFullYear() !== y ||
      localDate.getMonth() !== m - 1 ||
      localDate.getDate() !== d
    ) {
      return null;
    }
    return localDate;
  }

  // Otherwise let native parser handle full ISO or other formats with offsets
  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateRangeLabel(start: Date | null, end: Date | null): string {
  const currentYear = new Date().getFullYear();
  const format = (date: Date) => date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === currentYear ? {} : { year: 'numeric' }),
  });

  if (start && end) {
    return start.toDateString() === end.toDateString() ? format(start) : `${format(start)} – ${format(end)}`;
  }
  if (start) return `From ${format(start)}`;
  if (end) return `Through ${format(end)}`;
  return 'Date Range';
}

function UpdatedClientReport({
  canManageLeads = true,
  collaborators = [],
  defaultCards = [],
  fetchAnalyticsMetrics,
  fetchFastMetrics,
  initialEndDate,
  initialStartDate,
  preview = false,
  project,
  projectId
}: Props) {
  const buildoutOs = useBuildoutOsDisplay();
  const { width } = useWindowSize();

  const RESPONSIVE_BREAKPOINT = 1300;
  const isSmallScreen = width ? width < RESPONSIVE_BREAKPOINT : false;

  const [metrics, setMetrics] = useState<Metrics>();
  const [cards, setCards] = useState<CardProps[]>(setupCardsData(defaultCards));
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareableTokens, setShareableTokens] = useState<ShareableToken[]>([]);
  const [customCardModalOpen, setCustomCardModalOpen] = useState(false);
  const [currentCardId, setCurrentCardId] = useState<number | undefined>(undefined);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const [currentBody, setCurrentBody] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(normalizeDate(initialStartDate));
  const [endDate, setEndDate] = useState<Date | null>(normalizeDate(initialEndDate));
  const [fieldVisibilityOpen, setFieldVisibilityOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(undefined);

  const refreshShareableTokens = () => {
    fetchShareableTokens(projectId)
      .then((response) => {
        setShareableTokens(response.shareableTokens || []);
      });
  };

  const formatDate = (date: Date | null) => {
    return date === null ? '' : date.toLocaleDateString();
  };

  useEffect(() => {
    fetchFastMetrics(formatDate(startDate), formatDate(endDate)).then((fastMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...fastMetrics
      }));
    });
    refreshShareableTokens();
  }, [customCardModalOpen]);

  useEffect(() => {
    fetchFastMetrics(formatDate(startDate), formatDate(endDate)).then((fastMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...fastMetrics
      }));
    });
    fetchAnalyticsMetrics(formatDate(startDate), formatDate(endDate)).then((analyticsMetrics) => {
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        ...analyticsMetrics
      }));
    });
    refreshShareableTokens();
  }, [startDate, endDate]);

  useEffect(() => {
    if (!metrics?.customCards) return;

    const metricsCardIds = new Set(metrics.customCards.map(cc => cc.id));
    const existingCardIds = new Set(
      cards.filter(c => c.cardId != null).map(c => c.cardId)
    );
    const newCards = metrics.customCards
      .filter(cc => !existingCardIds.has(cc.id))
      .map(cc => ({
        cardType: 'customCard',
        displayCard: true,
        filters: {},
        cardId: cc.id,
      }));

    const filteredCards = cards.filter(
      c => c.cardType !== 'customCard' || (c.cardId != null && metricsCardIds.has(c.cardId))
    );

    const hasNew = newCards.length > 0;
    const hasRemoved = filteredCards.length < cards.length;

    if (hasNew || hasRemoved) {
      const updatedCards = [...filteredCards, ...newCards];
      setCards(updatedCards);
      saveCardData(updatedCards);
    }
  }, [metrics?.customCards]);

  const getCardFilters = (cardType: string) => {
    const card = cards.find(c => c.cardType === cardType);
    return card?.filters || {};
  };

  const getVisibleColumns = (cardType: string): string[] => {
    const card = cards.find(c => c.cardType === cardType);
    return card?.visibleColumns || [];
  };

  const getCardDisplayValue = (cardType: string): boolean => {
    const card = cards.find(c => c.cardType === cardType);
    return card?.displayCard !== false;
  };

  const getCardDisplayValueById = (cardId: number): boolean => {
    const card = cards.find(c => c.cardId === cardId);
    return card?.displayCard !== false;
  };

  const threeColWidth = fullSize => isSmallScreen ? 12 : fullSize;
  const twoColWidth = fullSize => isSmallScreen ? 12 : fullSize;

  const getVisibleCardsInRow = (rowCards: string[]) => {
    return rowCards.filter(card => getCardDisplayValue(card));
  };

  const getColWidth = (rowCards: string[]) => {
    const visibleCount = preview
      ? getVisibleCardsInRow(rowCards).length
      : rowCards.length;

    if (visibleCount === 0) return 0;
    if (visibleCount === 1) return 12;
    if (visibleCount === 2) return 6;
    return 4;
  };

  const basicMetricsRow = ['totalDaysOnMarket', 'totalCasSigned', 'totalLeads'];
  const basicColWidth = getColWidth(basicMetricsRow);

  const activityRow = ['activitySummary', 'activityFunnel', 'companyCaSummary'];
  const activityColWidth = getColWidth(activityRow);

  const getEmailAnalyticsColWidth = () => {
    if (!preview) return 4;

    const emailAnalyticsVisible = getCardDisplayValue('emailAnalytics');
    const emailDetailVisible = getCardDisplayValue('emailDetail');

    if (!emailAnalyticsVisible) return 0;
    return emailDetailVisible ? 4 : 12;
  };

  const websiteAndDocStatsRow = ['websiteTraffic', 'documentStats'];
  const websiteAndDocStatsColWidth = getColWidth(websiteAndDocStatsRow);

  const referralRow = ['referralSources', 'syndication'];
  const referralColWidth = getColWidth(referralRow);


  const handleCardUpdate = (
    cardType: string,
    updates: { filters?: any, displayCard?: boolean, visibleColumns?: string[] },
    cardId?: number
  ) => {
    const updatedCards = cards.map((card) => {
      const isMatch = cardId ? card.cardId === cardId : card.cardType === cardType;
      if (isMatch) {
        return {
          ...card,
          ...(updates.displayCard !== undefined ? { displayCard: updates.displayCard } : {}),
          ...(updates.filters ? {
            filters: {
              ...card.filters,
              ...updates.filters
            }
          } : {}),
          ...(updates.visibleColumns !== undefined ? { visibleColumns: updates.visibleColumns } : {})
        };
      }
      return card;
    });

    setCards(updatedCards);
    saveCardData(updatedCards);
  };

  const saveCardData = async (cards: CardProps[]): Promise<any> => {
    return put(`/projects/${projectId}/client_report`, { cards });
  };

  const handlePreviewClick = () => {
    const baseUrl = `/projects/${projectId}/client_report/preview`;
    window.open(baseUrl, '_blank');
  };

  // TODO: change toggle behavior to just show
  const toggleCustomCardModal = () => {
    setCustomCardModalOpen(!customCardModalOpen);
  };

  const addCustomCard = () => {
    setCurrentCardId(undefined);
    setCurrentTitle('');
    setCurrentSubtitle('');
    setCurrentBody('');
    toggleCustomCardModal();
  };

  const editCustomCard = (card: CustomCardProps) => {
    setCurrentCardId(card.id);
    setCurrentTitle(card.title || '');
    setCurrentSubtitle(card.subtitle || '');
    setCurrentBody(card.body || '');
    toggleCustomCardModal();
  };

  const customCardTitle = (id) => {
    const { customCards } = metrics || {};
    const card = customCards?.find(card => card.id === id);

    return card?.title;
  };

  const toggleVisibility = (e, card) => {
    e.stopPropagation();
    e.preventDefault();
    handleCardUpdate(card.cardType, { displayCard: !card.displayCard }, card.cardId);
  };

  const handleFieldVisibilityToggle = (isOpen, event, meta) => {
    // Allow closing when clicking the toggle button or clicking outside the menu
    if (isOpen) {
      setFieldVisibilityOpen(true);
    } else if (meta?.source === 'click' || meta?.source === 'rootClose') {
      setFieldVisibilityOpen(false);
    }
  };

  const toggleCardExpanded = (cardKey: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardKey)) {
        newSet.delete(cardKey);
      } else {
        newSet.add(cardKey);
      }
      return newSet;
    });
  };

  const setLeadsCardVisible = (visible: boolean) => {
    if (visible) {
      handleCardUpdate('leads', { displayCard: true, visibleColumns: Object.keys(leadsTableColumnsMap) });
    } else {
      handleCardUpdate('leads', { displayCard: false, visibleColumns: [] });
    }
  };

  const toggleLeadsCard = (card: CardProps) => setLeadsCardVisible(!card.displayCard);

  const toggleLeadsColumn = (visibleColumns: string[], columnKey: string) => {
    let newVisibleColumns: string[];
    if (visibleColumns.includes(columnKey)) {
      newVisibleColumns = visibleColumns.filter(k => k !== columnKey);
    } else {
      newVisibleColumns = [...visibleColumns, columnKey];
    }
    if (newVisibleColumns.length === 0) {
      handleCardUpdate('leads', { displayCard: false, visibleColumns: newVisibleColumns });
    } else {
      handleCardUpdate('leads', { displayCard: true, visibleColumns: newVisibleColumns });
    }
  };

  const cardVisibilityToggle = (card) => {
    const cardKey = `${card.cardType}-${card.cardId || 'no-id'}`;
    const isExpanded = expandedCards.has(cardKey);

    if (card.cardType === 'leads') {
      const visibleColumns = card.visibleColumns;

      const toggleColumnVisibility = (columnKey: string) => toggleLeadsColumn(visibleColumns, columnKey);

      return (
        <MenuItem
          className={isExpanded ? 'expanded-card' : ''}
          key={`field-visibility-${cardKey}-${card.displayCard}`}
          onClick={e => e.stopPropagation()}
        >
          <div className="d-flex flex-column w-100">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div
                className="d-flex align-items-center gap-2"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCardExpanded(cardKey);
                }}
              >
                <span>{cardMap[card.cardType]}</span>
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  style={{ width: '12px' }}
                />
              </div>
              <Toggle
                checked={card.displayCard}
                showText={false}
                small
                theme="ds"
                onChange={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleLeadsCard(card);
                }}
              />
            </div>
            {isExpanded && (
              <div className="d-flex flex-column gap-2 mt-2 ms-4">
                {Object.entries(leadsTableColumnsMap).map(([key, label]) => (
                  <div
                    className="d-flex justify-content-between align-items-center w-100"
                    key={`${key}-${visibleColumns.includes(key)}`}
                  >
                    <span>{label}</span>
                    <Toggle
                      checked={visibleColumns.includes(key)}
                      showText={false}
                      small
                      theme="ds"
                      onChange={() => toggleColumnVisibility(key)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </MenuItem>
      );
    }
    return (
      <MenuItem
        key={`field-visibility-${card.cardType}-${card.cardId || 'no-id'}-${card.displayCard}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{card.cardType === 'customCard' ? customCardTitle(card.cardId) : cardMap[card.cardType]}</span>
          <Toggle
            checked={card.displayCard}
            showText={false}
            small
            theme="ds"
            onChange={e => toggleVisibility(e, card)}
          />
        </div>
      </MenuItem>
    );
  };

  const handleDateRangeOpenChange = (open: boolean) => {
    setDateRangeOpen(open);
    setDraftRange(undefined);
  };

  const handleRangeSelect = (range: DateRange, day: Date) => {
    if (!draftRange?.from) {
      setDraftRange({ from: day, to: undefined });
      return;
    }

    setStartDate(range.from || null);
    setEndDate(range.to || null);
    handleDateRangeOpenChange(false);
  };

  const dateRangeLabel = formatDateRangeLabel(startDate, endDate);

  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    handleDateRangeOpenChange(false);
  };

  const dateRangePicker = (
    <Popover open={dateRangeOpen} onOpenChange={handleDateRangeOpenChange}>
      <Popover.Trigger
        render={(
          <Button
            aria-label={startDate || endDate ? `Date range: ${dateRangeLabel}` : undefined}
            icon={faCalendar}
            variant="outline"
          />
        )}
      >
        {dateRangeLabel}
      </Popover.Trigger>
      <Popover.Content align="start" className="p-0">
        <Calendar
          defaultMonth={startDate || endDate || undefined}
          mode="range"
          required
          selected={draftRange || { from: startDate || undefined, to: endDate || undefined }}
          onSelect={handleRangeSelect}
        />
        {(startDate || endDate) && (
          <div className="d-flex justify-content-end border-top p-2">
            <Button size="sm" variant="ghost" onClick={clearDateRange}>Clear</Button>
          </div>
        )}
      </Popover.Content>
    </Popover>
  );

  const fieldVisibilityCards = Object.keys(cardMap)
    .map(type => cards.find(c => c.cardType === type))
    .filter((card): card is CardProps => Boolean(card))
    .concat(cards.filter(c => !cardMap[c.cardType]));

  const fieldVisibilityRow = (card: CardProps) => {
    const cardKey = `${card.cardType}-${card.cardId || 'no-id'}`;
    const label = card.cardType === 'customCard' ? customCardTitle(card.cardId) : cardMap[card.cardType];

    if (card.cardType !== 'leads') {
      return (
        <Checkbox
          checked={card.displayCard !== false}
          key={cardKey}
          label={label}
          onCheckedChange={(checked: boolean) => handleCardUpdate(card.cardType, { displayCard: checked }, card.cardId)}
        />
      );
    }

    const isExpanded = expandedCards.has(cardKey);
    const visibleColumns = card.visibleColumns || [];

    return (
      <div className="d-flex flex-column gap-2" key={cardKey}>
        <div className="d-flex align-items-center justify-content-between gap-2">
          <Checkbox checked={card.displayCard !== false} label={label} onCheckedChange={setLeadsCardVisible} />
          <Button
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} ${label} columns`}
            icon={isExpanded ? faChevronDown : faChevronRight}
            size="sm"
            variant="ghost"
            onClick={() => toggleCardExpanded(cardKey)}
          />
        </div>
        {isExpanded && (
          <div className="d-flex flex-column gap-2 ms-4">
            {Object.entries(leadsTableColumnsMap).map(([key, columnLabel]) => (
              <Checkbox
                checked={visibleColumns.includes(key)}
                key={key}
                label={columnLabel}
                onCheckedChange={() => toggleLeadsColumn(visibleColumns, key)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const fieldVisibilityPopover = (
    <Popover>
      <Popover.Trigger render={<Button icon={faEyeOutline} variant="outline" />}>
        Field Visibility
      </Popover.Trigger>
      <Popover.Content align="end" className="overflow-auto" style={{ maxHeight: 'var(--available-height)' }}>
        <Popover.Body className="d-flex flex-column gap-2">
          <Alert icon={faExclamationTriangle} severity="warning">
            <span>These settings apply to all active preview links for this listing.</span>
          </Alert>
          {fieldVisibilityCards.map(card => fieldVisibilityRow(card))}
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );

  const pageHeaderActions = (
    <PageHeader.Actions>
      <div className={classNames('d-flex align-items-center', buildoutOs ? 'gap-2' : 'gap-3')}>
        {buildoutOs && dateRangePicker}
        {buildoutOs ? fieldVisibilityPopover : (
          <Dropdown
            className="actions-dropdown"
            icon={faEye}
            id="actions-field-visibility-dropdown"
            label="Field Visibility"
            open={fieldVisibilityOpen}
            variant="secondary"
            onToggle={handleFieldVisibilityToggle}
          >
            <Alert className="m-3" icon={faExclamationTriangle} severity="warning">
              <span>These settings apply to all active preview links for this listing.</span>
            </Alert>
            {fieldVisibilityCards.map(card => cardVisibilityToggle(card))}
          </Dropdown>
        )}
        <Dropdown
          className="actions-dropdown"
          id="actions-dropdown"
          label="Actions"
          variant={buildoutOs ? 'outline' : 'primary'}
        >
          <MenuItem
            key="preview-action"
            onClick={handlePreviewClick}
          >
            Preview
          </MenuItem>
          <MenuItem
            key="add-custom-card"
            onClick={addCustomCard}
          >
            Add Custom Card
          </MenuItem>
        </Dropdown>
        <Button icon={faPaperPlane} onClick={() => setShowShareModal(true)}>Share Summary</Button>
      </div>
    </PageHeader.Actions>
  );

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const dateSelector = (
    <div className="d-flex align-items-center gap-3">
      <div>
        <DatePicker
          className="form-control"
          endDate={endDate}
          maxDate={endDate || undefined}
          placeholderText="Start Date"
          selected={startDate}
          selectsStart
          showMonthDropdown
          showYearDropdown
          startDate={startDate}
          onChange={(date: Date | null) => setStartDate(date)}
        />
      </div>
      <div>
        <DatePicker
          className="form-control"
          endDate={endDate}
          minDate={startDate || undefined}
          placeholderText="End Date"
          selected={endDate}
          selectsEnd
          showMonthDropdown
          showYearDropdown
          startDate={startDate}
          onChange={(date: Date | null) => setEndDate(date)}
        />
      </div>
      {(startDate || endDate) &&
        <Button
          className="text-decoration-none"
          icon={faXmark}
          style={{ cursor: 'pointer' }}
          variant="ghost"
          onClick={clearDateFilters}
        >
          Clear All
        </Button>
      }
    </div>
  );

  const renderDaysOnMarket = () => {
    const metricsAvailable = metrics !== undefined && metrics.daysOnMarket !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalDaysOnMarket');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalDaysOnMarket')}
            preview={preview}
            text="Total Days on Market"
            value={metrics?.daysOnMarket}
            onPreviewChange={isHidden => handleCardUpdate('totalDaysOnMarket', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderSignedCas = () => {
    const metricsAvailable = metrics !== undefined && metrics.casSigned !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalCasSigned');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalCasSigned')}
            preview={preview}
            text="CA's Signed"
            value={metrics.casSigned}
            onPreviewChange={isHidden => handleCardUpdate('totalCasSigned', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderTotalLeads = () => {
    const metricsAvailable = metrics !== undefined && metrics.totalLeads !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('totalLeads');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={threeColWidth(basicColWidth)}>
          <BasicCardWithPreview
            displayCard={getCardDisplayValue('totalLeads')}
            preview={preview}
            text="Leads"
            value={metrics.totalLeads}
            onPreviewChange={isHidden => handleCardUpdate('totalLeads', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderActivitySummary = () => {
    const metricsAvailable = metrics !== undefined && metrics.activitySummary !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('activitySummary');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <ActivitySummaryCardWithPreview
            activitySummary={metrics.activitySummary}
            displayCard={getCardDisplayValue('activitySummary')}
            preview={preview}
            projectId={projectId}
            onPreviewChange={isHidden => handleCardUpdate('activitySummary', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderActivityFunnel = () => {
    const metricsAvailable = metrics !== undefined
      && metrics.casSigned !== undefined
      && metrics.casViewed !== undefined
      && metrics.documentDownloads !== undefined
      && metrics.documentViews !== undefined
      && metrics.emailAnalytics !== undefined
      && metrics.websiteViews !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('activityFunnel');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <ActivityFunnelWithPreview
            casSigned={metrics.casSigned}
            casViewed={metrics.casViewed}
            displayCard={getCardDisplayValue('activityFunnel')}
            documentDownloads={metrics.documentDownloads}
            documentViews={metrics.documentViews}
            emailAnalytics={metrics.emailAnalytics}
            preview={preview}
            totalPublicDocViews={metrics.totalPublicDocViews}
            uniqueLockedViews={metrics.uniqueLockedViews}
            websiteViews={metrics.websiteViews}
            onPreviewChange={isHidden => handleCardUpdate('activityFunnel', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderCompaniesCard = () => {
    const metricsAvailable = metrics !== undefined && metrics.leadCountByCompanyName !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('companyCaSummary');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={threeColWidth(activityColWidth)}>
          <CompanyCardWithPreview
            displayCard={getCardDisplayValue('companyCaSummary')}
            leadCountByCompanyName={metrics.leadCountByCompanyName}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('companyCaSummary', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };


  const renderDocumentStats = () => {
    const metricsAvailable =
      metrics !== undefined &&
      metrics.documentDownloadsByMonth !== undefined &&
      metrics.documentViewsByMonth !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('documentStats');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(websiteAndDocStatsColWidth)}>
          <DocumentStatsCardWithPreview
            displayCard={getCardDisplayValue('documentStats')}
            documentDownloadsByMonth={metrics.documentDownloadsByMonth}
            documentViewsByMonth={metrics.documentViewsByMonth}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('documentStats', { displayCard: !isHidden })}
          />
        </Col>
      );
    }
    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderLeads = () => {
    const metricsAvailable = metrics !== undefined && metrics.leads !== undefined && metrics.leadFilters !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('leads');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4" md={12}>
          <LeadsCardWithPreview
            canManageLeads={canManageLeads}
            displayCard={getCardDisplayValue('leads')}
            filters={getCardFilters('leads')}
            leadFilters={metrics.leadFilters}
            leads={metrics.leads}
            preview={preview}
            projectId={projectId}
            visibleColumns={getVisibleColumns('leads')}
            onFilterUpdate={filters => handleCardUpdate('leads', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('leads', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderEmailAnalytics = () => {
    const metricsAvailable = metrics !== undefined && metrics.emailAnalytics !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('emailAnalytics');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={getEmailAnalyticsColWidth()}>
          <EmailAnalyticsWithPreview
            displayCard={getCardDisplayValue('emailAnalytics')}
            emailAnalytics={metrics.emailAnalytics}
            filters={getCardFilters('emailAnalytics')}
            preview={preview}
            onFilterUpdate={filters => handleCardUpdate('emailAnalytics', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('emailAnalytics', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderEmailDetail = () => {
    const metricsAvailable = metrics !== undefined && metrics.emailAnalytics !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('emailDetail');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={MAX_NUM_COLUMNS - getEmailAnalyticsColWidth()}>
          <EmailDetailsWithPreview
            displayCard={getCardDisplayValue('emailDetail')}
            emailAnalytics={metrics.emailAnalytics}
            preview={preview}
            onPreviewChange={isHidden => handleCardUpdate('emailDetail', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderWebsiteTraffic = () => {
    const metricsAvailable = metrics !== undefined
      && metrics.timeOnPage !== undefined
      && metrics.uniqueVisitors !== undefined
      && metrics.websiteViews !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('websiteTraffic');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(websiteAndDocStatsColWidth)}>
          <WebsiteTrafficWithPreview
            displayCard={getCardDisplayValue('websiteTraffic')}
            filters={getCardFilters('websiteTraffic')}
            preview={preview}
            timeOnPage={metrics.timeOnPage}
            uniqueVisitors={metrics.uniqueVisitors}
            websiteViews={metrics.websiteViews}
            onFilterUpdate={filters => handleCardUpdate('websiteTraffic', { filters })}
            onPreviewChange={isHidden => handleCardUpdate('websiteTraffic', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderReferralSourceCard = () => {
    const metricsAvailable = metrics !== undefined && metrics.referralCountBySource !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('referralSources');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={twoColWidth(referralColWidth)}>
          <ReferralSourceCardWithPreview
            displayCard={getCardDisplayValue('referralSources')}
            preview={preview}
            referralCountBySource={metrics.referralCountBySource}
            onPreviewChange={isHidden => handleCardUpdate('referralSources', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const renderSyndicationPartners = () => {
    const metricsAvailable = metrics !== undefined && metrics.syndicationPartners !== undefined;
    const shouldDisplay = !preview || getCardDisplayValue('syndication');

    if (shouldDisplay && metricsAvailable) {
      return (
        <Col className="mb-4 ds-col-flex" md={(twoColWidth(referralColWidth))}>
          <SyndicationCardWithPreview
            displayCard={getCardDisplayValue('syndication')}
            preview={preview}
            syndicationPartners={metrics.syndicationPartners}
            onPreviewChange={isHidden => handleCardUpdate('syndication', { displayCard: !isHidden })}
          />
        </Col>
      );
    }

    if (shouldDisplay && !metricsAvailable) return <LoadingCard />;

    return null;
  };

  const customColWidth = (cardCount: number) => {
    if (isSmallScreen) return 12;
    if (!preview) return 4;
    if (cardCount === 1) return 12;
    if (cardCount === 2) return 6;
    return 4;
  };

  const customCardRow = (cards: CustomCardProps[]) => {
    // Card row
    const displayedCards = cards.filter(card => getCardDisplayValueById(card.id) === true || !preview);
    const displayedCardsCount = displayedCards.length;

    return (
      <div className="row align-items-start mx-1 mx-md-0">
        {displayedCards.map(card => (
          <Col className="mb-4" key={card.id} md={customColWidth(displayedCardsCount)}>
            <CustomCardWithPreview
              key={card.id}
              {...card}
              displayCard={getCardDisplayValueById(card.id)}
              editCard={() => editCustomCard(card)}
              preview={preview}
              onPreviewChange={isHidden => handleCardUpdate('custom', { displayCard: !isHidden }, card.id)}
            />
          </Col>
        ))}
      </div>
    );
  };

  const renderCustomCards = () => {
    const { customCards } = metrics || {};
    if (!customCards || customCards.length === 0) return null;

    if (isSmallScreen) {
      return customCards.map(card => customCardRow([card]));
    }

    // Split customCards into groups of three and render each row
    const rows: React.JSX.Element[] = [];
    for (let i = 0; i < customCards.length; i += 3) {
      rows.push(customCardRow(customCards.slice(i, i + 3)));
    }
    return <>{rows}</>;
  };

  return (
    <div className="updated-client-report" data-buildout-os={buildoutOs || undefined}>
      <ShareClientReportModal
        collaborators={collaborators}
        projectId={projectId}
        shareableTokens={shareableTokens}
        show={showShareModal}
        onCloseModal={() => setShowShareModal(false)}
        onTokensChanged={refreshShareableTokens}
      />
      {customCardModalOpen && (
        <CustomCardModal
          cardId={currentCardId}
          currentBody={currentBody}
          currentSubtitle={currentSubtitle}
          currentTitle={currentTitle}
          modalOpen={customCardModalOpen}
          projectId={projectId}
          onCloseModal={() => setCustomCardModalOpen(false)}
        />
      )}
      <div>
        {!preview &&
          <div className="new-header-design">
            <Row className="align-items-center mb-2">
              <Col>
                <h5>Client Report</h5>
              </Col>
              <Col xs="auto">
                {pageHeaderActions}
              </Col>
            </Row>
            {!buildoutOs && <div>{dateSelector}</div>}
          </div>}
        {
          // Remove the styling in the div below when we remove the customization banner
        }
        <div className="d-flex flex-column gap-4" style={{ paddingBottom: '40px' }}>
          <div className="row mx-1 mx-md-0">
            {renderDaysOnMarket()}
            {renderSignedCas()}
            {renderTotalLeads()}
          </div>

          <div className="row mx-1 mx-md-0">
            {renderActivitySummary()}
            {renderActivityFunnel()}
            {renderCompaniesCard()}
          </div>

          <div className="row mx-1 mx-md-0">
            {renderLeads()}
          </div>

          <div className="row mx-1 mx-md-0">
            {renderEmailAnalytics()}
            {renderEmailDetail()}
          </div>

          <div className="row mx-1 mx-md-0">
            {renderWebsiteTraffic()}
            {renderDocumentStats()}
          </div>

          <div className="row mx-1 mx-md-0">
            {renderReferralSourceCard()}
            {renderSyndicationPartners()}
          </div>

          {renderCustomCards()}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  projectId: state.project.id,
});

export { UpdatedClientReport };
export default connect(mapStateToProps)(UpdatedClientReport);
